import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Car, Image, RefreshCw, CheckCircle, AlertCircle, 
  Loader2, Download, Trash2, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VehicleImage from '../../components/ui/VehicleImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVehicleImages = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingVehicle, setGeneratingVehicle] = useState(null); // Track which vehicle is generating
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [allImages, setAllImages] = useState([]);  // NEW: All images from DB
  const [vehicleImages, setVehicleImages] = useState({});
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      // Get image stats, vehicles, and all images in parallel
      const [statsRes, vehiclesRes, imagesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/vehicle-image-stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/vehicles`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/all-vehicle-images`, { headers })
      ]);
      
      setStats(statsRes.data);
      setAllImages(imagesRes.data?.images || []);
      
      // Group by brand/model to get unique combinations
      const uniqueVehicles = {};
      (vehiclesRes.data || []).forEach(v => {
        const key = `${v.brand}_${v.model}`;
        if (!uniqueVehicles[key]) {
          uniqueVehicles[key] = {
            id: v.id,
            brand: v.brand,
            model: v.model,
            count: 1
          };
        } else {
          uniqueVehicles[key].count++;
        }
      });
      
      const vehicleList = Object.values(uniqueVehicles);
      setVehicles(vehicleList);
      
      // Load images for each vehicle IN PARALLEL
      const imagePromises = vehicleList.map(async (v) => {
        try {
          const imgRes = await axios.get(
            `${BACKEND_URL}/api/vehicles/${v.id}/image`,
            { headers, timeout: 10000 }
          );
          // Only count as having image if has_image is true
          if (imgRes.data?.has_image && imgRes.data?.image_url) {
            return { key: `${v.brand}_${v.model}`, url: imgRes.data.image_url };
          }
        } catch (e) {
          // No image yet - this is expected
        }
        return null;
      });
      
      const imageResults = await Promise.all(imagePromises);
      const images = {};
      imageResults.filter(Boolean).forEach(({ key, url }) => {
        images[key] = url;
      });
      setVehicleImages(images);
      
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/generate-vehicle-images`,
        {},
        { headers }
      );
      
      if (response.data.status === 'error') {
        toast.error(response.data.message);
        setGenerating(false);
        return;
      }
      
      if (response.data.status === 'complete') {
        toast.success(response.data.message);
        setGenerating(false);
        return;
      }
      
      toast.success(response.data.message);
      toast.info(response.data.note || 'Die Generierung läuft im Hintergrund...');
      
      // Poll for completion every 30 seconds
      const pollInterval = setInterval(async () => {
        await loadData();
        // Check if all images are generated
        if (stats?.vehicles_without_images === 0) {
          clearInterval(pollInterval);
          setGenerating(false);
          toast.success('Alle Bilder wurden generiert!');
        }
      }, 30000);
      
      // Stop polling after 10 minutes max
      setTimeout(() => {
        clearInterval(pollInterval);
        setGenerating(false);
        loadData();
      }, 600000);
      
    } catch (error) {
      console.error('Failed to generate images:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Bildgenerierung');
      setGenerating(false);
    }
  };
  
  const handleGenerateSingle = async (vehicleId, brand, model) => {
    const vehicleKey = `${brand}_${model}`;
    setGeneratingVehicle(vehicleKey);
    try {
      toast.info(`Generiere Bild für ${brand} ${model}... (ca. 30 Sekunden)`);
      
      // Use the dedicated generation endpoint
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/generate-single-vehicle-image`,
        { brand, model },
        { headers, timeout: 120000 }  // 2 minute timeout for image generation
      );
      
      if (response.data?.status === 'success') {
        toast.success(response.data.message);
        setVehicleImages(prev => ({
          ...prev,
          [vehicleKey]: response.data.image_url
        }));
        // Reload stats
        loadData();
      } else {
        toast.error(response.data?.message || `Fehler beim Generieren des Bildes für ${brand} ${model}`);
      }
    } catch (error) {
      console.error('Single image generation error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Timeout - die Generierung dauert zu lange. Bitte versuchen Sie es erneut.');
      } else {
        toast.error(error.response?.data?.detail || `Fehler beim Generieren des Bildes für ${brand} ${model}`);
      }
    } finally {
      setGeneratingVehicle(null);
    }
  };
  
  const getImageUrl = (vehicleId, brand, model) => {
    const key = `${brand}_${model}`;
    if (vehicleImages[key]) {
      return `${BACKEND_URL}/api/vehicles/${vehicleId}/image-file`;
    }
    return null;
  };
  
  if (loading) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }
  
  return (
    <div className="p-6 min-h-screen" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="p-2 rounded-xl bg-cyan-500/20 border border-cyan-400/30">
              <Image className="w-8 h-8 text-cyan-400" />
            </div>
            Fahrzeugbilder verwalten
          </h1>
          <p className="text-slate-400">Automatische Bildgenerierung für alle Fahrzeuge mit GPT Image</p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-cyan-500/20">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-cyan-500/20 border border-cyan-400/30">
                <Car className="w-6 h-6 text-cyan-300" />
              </div>
              <div>
                <p className="text-sm text-cyan-200/70">Fahrzeuge (unique)</p>
                <p className="text-3xl font-bold text-white">{stats?.total_unique_vehicles || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-green-500/20">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-500/20 border border-green-400/30">
                <Image className="w-6 h-6 text-green-300" />
              </div>
              <div>
                <p className="text-sm text-green-200/70">Bilder vorhanden</p>
                <p className="text-3xl font-bold text-white">{stats?.total_images || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-purple-500/20">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-500/20 border border-purple-400/30">
                <CheckCircle className="w-6 h-6 text-purple-300" />
              </div>
              <div>
                <p className="text-sm text-purple-200/70">Abdeckung</p>
                <p className="text-3xl font-bold text-white">{stats?.coverage_percent || 0}%</p>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Generate All Button */}
        <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">
                Alle fehlenden Bilder generieren
              </h3>
              <p className="text-sm text-slate-400">
                Generiert automatisch Bilder für alle Fahrzeuge ohne Bild. 
                Dauert ca. 30-60 Sekunden pro Fahrzeug.
              </p>
              {stats?.vehicles_without_images > 0 && (
                <p className="text-sm text-amber-400 mt-2">
                  {stats.vehicles_without_images} Fahrzeug{stats.vehicles_without_images > 1 ? 'e' : ''} ohne Bild gefunden
                </p>
              )}
            </div>
            <Button
              onClick={handleGenerateAll}
              disabled={generating || (stats?.coverage_percent === 100 && stats?.vehicles_without_images === 0)}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 px-6"
            >
              {generating ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generiere...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Bilder generieren
                </span>
              )}
            </Button>
          </div>
          
          {stats?.coverage_percent === 100 && stats?.vehicles_without_images === 0 && (
            <div className="mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-300 text-sm">Alle Fahrzeuge haben bereits ein Bild!</span>
            </div>
          )}
        </Card>
        
        {/* Vehicle List */}
        <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span>Fahrzeuge ({vehicles.length})</span>
              <Button variant="ghost" size="sm" onClick={loadData} className="text-slate-400 hover:text-white">
                <RefreshCw className="w-4 h-4 mr-2" />
                Aktualisieren
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {vehicles.map((vehicle) => {
                const key = `${vehicle.brand}_${vehicle.model}`;
                const hasImage = !!vehicleImages[key];
                const imageUrl = getImageUrl(vehicle.id, vehicle.brand, vehicle.model);
                
                return (
                  <div 
                    key={key}
                    className="flex items-center gap-4 p-4 rounded-xl border transition-colors"
                    style={{ 
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      borderColor: hasImage ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)'
                    }}
                  >
                    {/* Image Preview */}
                    <VehicleImage 
                      vehicleId={vehicle.id}
                      brand={vehicle.brand}
                      model={vehicle.model}
                      size="sm"
                      className="w-20 h-14 flex-shrink-0"
                    />
                    
                    {/* Vehicle Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-white">
                        {vehicle.brand} {vehicle.model}
                      </h4>
                      <p className="text-xs text-slate-400">
                        {vehicle.count} Fahrzeug{vehicle.count > 1 ? 'e' : ''} registriert
                      </p>
                    </div>
                    
                    {/* Status & Actions */}
                    <div className="flex items-center gap-3">
                      {hasImage ? (
                        <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-400/30 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Bild vorhanden
                        </span>
                      ) : (
                        <>
                          <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-400/30 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Kein Bild
                          </span>
                          <Button
                            size="sm"
                            onClick={() => handleGenerateSingle(vehicle.id, vehicle.brand, vehicle.model)}
                            disabled={generatingVehicle === key || generating}
                            className="bg-cyan-500/20 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-500/30 disabled:opacity-50"
                          >
                            {generatingVehicle === key ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                Generiert...
                              </>
                            ) : (
                              <>
                                <Image className="w-4 h-4 mr-1" />
                                Generieren
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {vehicles.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Car className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Keine registrierten Fahrzeuge vorhanden</p>
                  <p className="text-xs mt-2">Generierte Bilder werden unten angezeigt</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Generated Images Gallery */}
        <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span>Generierte Bilder ({allImages.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allImages.map((img, idx) => (
                <div 
                  key={`${img.brand}_${img.model}_${idx}`}
                  className="rounded-xl overflow-hidden border border-white/10 bg-white/5"
                >
                  <div className="aspect-video bg-slate-800 flex items-center justify-center">
                    {img.image_path ? (
                      <img 
                        src={`${BACKEND_URL}/api${img.image_path}`}
                        alt={`${img.brand} ${img.model}`}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="hidden items-center justify-center w-full h-full">
                      <Car className="w-8 h-8 text-slate-500" />
                    </div>
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-medium text-white truncate">
                      {img.brand} {img.model}
                    </h4>
                    <p className="text-xs text-slate-400 mt-1">
                      {img.created_at ? new Date(img.created_at).toLocaleDateString('de-DE') : 'Unbekannt'}
                    </p>
                  </div>
                </div>
              ))}
              
              {allImages.length === 0 && (
                <div className="col-span-full text-center py-8 text-slate-400">
                  <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Noch keine Bilder generiert</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Info Box */}
        <div className="mt-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
          <h4 className="font-semibold text-blue-300 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Hinweis zur Bildgenerierung
          </h4>
          <ul className="text-sm text-slate-300 space-y-1">
            <li>• Bilder werden mit GPT Image (DALL-E) generiert</li>
            <li>• Alle Fahrzeuge werden als weiße Autos in 3/4-Ansicht dargestellt</li>
            <li>• Die Generierung verbraucht Emergent LLM Credits</li>
            <li>• Generierte Bilder werden lokal gespeichert und können wiederverwendet werden</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminVehicleImages;
